import React, { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../../shared/services/supabase';

// Panel "Aktive Mitarbeiter des Monats in den Spiegel laden".
//
// Dritter Ablauf im DATEV-Bereich eines Mandanten, bewusst als eigene, in sich
// geschlossene Komponente gehalten, damit die bestehenden Abläufe in
// DatevExchange.tsx unangetastet bleiben.
//
// Klick stösst EINMALIG die Edge Function datev-employee-scoped-sync-enqueue-manual
// an und pollt danach datev-employee-scoped-status im Sekundentakt für die
// Fortschrittsanzeige. Der Worker arbeitet serverseitig ab; hier wird nichts
// live bei DATEV abgefragt.
//
// Zwei Modi, serverseitig bestimmt. Das Panel wählt nicht aus, es zeigt an:
//   'einzeln'     — eine DATEV-Abfrage je Mitarbeiter der Monatskohorte,
//                   einschliesslich der bereits gespiegelten (Auffrischung).
//                   Fortschritt ist die Zahl der abgearbeiteten Jobs.
//   'vollbestand' — eine DATEV-Abfrage je Mandant und Monat. Ein Jobzähler wäre
//                   sinnlos, deshalb misst der Fortschritt hier, wie viele
//                   Mitarbeiter der Monatskohorte bereits im Spiegel stehen.
//
// Ein Mandant kann per `customers.datev_sync_modus` fest auf eine Betriebsart
// gelegt sein. Das geschieht unter anderem automatisch, wenn DATEV den
// Bestandsabruf mit DCO02430 (Antwort zu gross) ablehnt. Das Panel weist
// darauf hin, damit ein abweichend laufender Mandant nicht unbemerkt bleibt.

interface Props {
  customerId: string;
  beraternummer?: string | null;
  mandantennummer?: string | null;
}

interface EnqueueErgebnis {
  ok: boolean;
  error?: string;
  hinweis?: string;
  modus?: string;
  mandant_modus?: string | null;
  kohorte?: number;
  bereits_gespiegelt?: number;
  bereits_aktiv?: number;
  neu_angelegt?: number;
}

interface BestandStatus {
  status?: string | null;
  versuch_anzahl?: number;
  poll_count?: number;
  mitarbeiter_gespiegelt?: number | null;
  last_error?: string | null;
  gestartet_am?: string | null;
  abgeschlossen_am?: string | null;
}

interface StatusErgebnis {
  ok: boolean;
  error?: string;
  modus?: string;
  mandant_modus?: string | null;
  gesamt?: number;
  in_arbeit?: number;
  fertig?: boolean;
  nach_status?: Record<string, number>;
  kohorte?: number;
  gespiegelt?: number;
  bestand?: BestandStatus | null;
}

// Zuletzt abgeschlossener Monat (aktueller Monat minus 1) in Europe/Berlin,
// als "YYYY-MM" für das Monatsfeld.
function letzterAbgeschlossenerMonat(): string {
  const jetzt = new Date();
  const berlin = new Date(jetzt.toLocaleString('en-US', { timeZone: 'Europe/Berlin' }));
  berlin.setDate(1);
  berlin.setMonth(berlin.getMonth() - 1);
  const jahr = berlin.getFullYear();
  const monat = String(berlin.getMonth() + 1).padStart(2, '0');
  return `${jahr}-${monat}`;
}

// Klartext für den Zustand des Mandanten-Bestandsauftrags.
function bestandStatusText(status?: string | null): string {
  switch (status) {
    case 'neu': return 'wartet auf Start';
    case 'startet': return 'wird gestartet';
    case 'offen': return 'bei DATEV in Arbeit';
    case 'erfolgreich': return 'erfolgreich';
    case 'fehlgeschlagen': return 'fehlgeschlagen';
    case 'timeout': return 'Zeitüberschreitung';
    case 'abgebrochen': return 'abgebrochen';
    case 'zu_gross': return 'Bestand zu groß für den Gesamtabruf';
    default: return status ? String(status) : 'unbekannt';
  }
}

const POLL_INTERVALL_MS = 3000;

const ScopedMonthEmployeeSyncPanel: React.FC<Props> = ({ customerId, beraternummer, mandantennummer }) => {
  const [monat, setMonat] = useState<string>(letzterAbgeschlossenerMonat());
  const [laeuft, setLaeuft] = useState(false);
  const [gestartet, setGestartet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [enqueue, setEnqueue] = useState<EnqueueErgebnis | null>(null);
  const [status, setStatus] = useState<StatusErgebnis | null>(null);

  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const holeStatus = useCallback(async () => {
    const { data, error } = await supabase.functions.invoke('datev-employee-scoped-status', {
      body: { customerId, month: monat }
    });
    if (error) return;
    const res = data as StatusErgebnis;
    if (!res?.ok) return;
    setStatus(res);
    if (res.fertig) {
      stopPolling();
      setLaeuft(false);
    }
  }, [customerId, monat, stopPolling]);

  const starten = useCallback(async () => {
    setFehler(null);
    setEnqueue(null);
    setStatus(null);
    setLaeuft(true);
    setGestartet(true);
    try {
      const { data, error } = await supabase.functions.invoke('datev-employee-scoped-sync-enqueue-manual', {
        body: { customerId, month: monat }
      });
      if (error) {
        setFehler(error.message || 'Aufruf fehlgeschlagen');
        setLaeuft(false);
        return;
      }
      const res = data as EnqueueErgebnis;
      if (!res?.ok) {
        setFehler(res?.error || 'Anlegen fehlgeschlagen');
        setLaeuft(false);
        return;
      }
      setEnqueue(res);

      // Nur pollen, wenn tatsächlich etwas anzulegen war oder etwas offen ist.
      if ((res.neu_angelegt ?? 0) > 0) {
        await holeStatus();
        stopPolling();
        pollTimer.current = setInterval(holeStatus, POLL_INTERVALL_MS);
      } else {
        // Nichts angelegt: trotzdem einmal den aktuellen Stand zeigen, aber
        // nicht dauerpollen.
        await holeStatus();
        setLaeuft(false);
      }
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
      setLaeuft(false);
    }
  }, [customerId, monat, holeStatus, stopPolling]);

  // Modus kommt vom Server. Vor dem ersten Aufruf ist er unbekannt, dann gilt
  // die bisherige Einzeldarstellung.
  const istVoll = (status?.modus ?? enqueue?.modus) === 'vollbestand';
  const mandantModus = status?.mandant_modus ?? enqueue?.mandant_modus ?? null;

  const s = status?.nach_status ?? {};
  const erfolgreich = s.erfolgreich ?? 0;
  const fehlgeschlagen = s.fehlgeschlagen ?? 0;
  const timeout = s.timeout ?? 0;
  const abgebrochen = s.abgebrochen ?? 0;
  const inArbeit = (s.neu ?? 0) + (s.offen ?? 0);
  const gesamt = status?.gesamt ?? 0;
  const erledigt = erfolgreich + fehlgeschlagen + timeout + abgebrochen;
  const prozent = gesamt > 0 ? Math.round((erledigt / gesamt) * 100) : 0;

  // Vollmodus: Fortschritt misst den Spiegelstand gegen die Monatskohorte.
  const bestand = status?.bestand ?? null;
  const kohorteVoll = status?.kohorte ?? 0;
  const gespiegeltVoll = status?.gespiegelt ?? 0;
  const prozentVoll = kohorteVoll > 0 ? Math.round((gespiegeltVoll / kohorteVoll) * 100) : 0;

  return (
    <div className="mt-6 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-1 flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900">
          Aktive Mitarbeiter des Monats in den Spiegel laden
        </h3>
        {beraternummer && mandantennummer && (
          <span className="text-xs text-gray-400">
            {beraternummer}-{mandantennummer}
          </span>
        )}
      </div>
      <p className="mb-4 text-sm text-gray-500">
        {istVoll
          ? 'Lädt den kompletten Mitarbeiterbestand des Mandanten in einem einzigen DATEV-Auftrag und aktualisiert dabei auch bereits vorhandene Stammdaten. Der Abruf läuft im Hintergrund weiter, auch wenn diese Seite geschlossen wird.'
          : 'Lädt die Stammdaten aller Mitarbeiter mit Lohnauswertung im gewählten Monat einzeln über DATEV. Bereits vorhandene Mitarbeiter werden dabei aufgefrischt. Der Abruf läuft im Hintergrund weiter, auch wenn diese Seite geschlossen wird.'}
      </p>

      {mandantModus === 'einzeln' && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Dieser Mandant ist fest auf Einzelabfragen eingestellt. Der Gesamtabruf
          ist hier nicht möglich, weil DATEV die Antwort für diesen Bestand als zu
          groß ablehnt.
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-sm text-gray-600">
          <span className="mb-1">Monat</span>
          <input
            type="month"
            value={monat}
            onChange={(e) => setMonat(e.target.value)}
            disabled={laeuft}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
          />
        </label>
        <button
          type="button"
          onClick={starten}
          disabled={laeuft || !monat}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          {laeuft ? 'Läuft …' : gestartet ? 'Erneut laden' : 'Laden starten'}
        </button>
      </div>

      {fehler && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {fehler}
        </div>
      )}

      {enqueue && (
        <div className="mt-4 text-sm text-gray-700">
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            <span>Kohorte: <strong>{enqueue.kohorte ?? 0}</strong></span>
            <span>bereits gespiegelt: <strong>{enqueue.bereits_gespiegelt ?? 0}</strong></span>
            {istVoll
              ? <span>Auftrag angelegt: <strong>{(enqueue.neu_angelegt ?? 0) > 0 ? 'ja' : 'nein'}</strong></span>
              : <span>eingeplant: <strong>{enqueue.neu_angelegt ?? 0}</strong></span>}
          </div>
          {enqueue.hinweis && <p className="mt-1 text-gray-500">{enqueue.hinweis}</p>}
        </div>
      )}

      {istVoll && bestand && (
        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between text-xs text-gray-500">
            <span>{gespiegeltVoll} von {kohorteVoll} Mitarbeitern des Monats im Spiegel</span>
            <span>{prozentVoll}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full rounded-full bg-blue-600 transition-all duration-500"
              style={{ width: `${prozentVoll}%` }}
            />
          </div>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm">
            <span className={bestand.status === 'zu_gross' ? 'text-amber-700' : 'text-gray-600'}>
              Auftrag: {bestandStatusText(bestand.status)}
            </span>
            {(bestand.versuch_anzahl ?? 0) > 0 && (
              <span className="text-gray-500">Versuch {bestand.versuch_anzahl}</span>
            )}
            {typeof bestand.mitarbeiter_gespiegelt === 'number' && (
              <span className="text-green-700">geliefert: {bestand.mitarbeiter_gespiegelt}</span>
            )}
          </div>
          {bestand.status === 'zu_gross' && (
            <p className="mt-2 text-sm text-amber-700">
              Der Mandant wurde automatisch auf Einzelabfragen umgestellt. Ein
              erneuter Klick auf "Erneut laden" fragt die Mitarbeiter einzeln ab.
            </p>
          )}
          {bestand.status !== 'zu_gross' && bestand.last_error && (
            <p className="mt-2 text-sm text-amber-700">{bestand.last_error}</p>
          )}
          {status?.fertig && bestand.status === 'erfolgreich' && (
            <p className="mt-2 text-sm font-medium text-green-700">Fertig.</p>
          )}
        </div>
      )}

      {!istVoll && gesamt > 0 && (
        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between text-xs text-gray-500">
            <span>{erledigt} von {gesamt} verarbeitet</span>
            <span>{prozent}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full rounded-full bg-blue-600 transition-all duration-500"
              style={{ width: `${prozent}%` }}
            />
          </div>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm">
            <span className="text-green-700">erfolgreich: {erfolgreich}</span>
            <span className="text-gray-600">in Arbeit: {inArbeit}</span>
            {fehlgeschlagen > 0 && <span className="text-red-700">fehlgeschlagen: {fehlgeschlagen}</span>}
            {timeout > 0 && <span className="text-amber-700">Zeitüberschreitung: {timeout}</span>}
            {abgebrochen > 0 && <span className="text-gray-500">abgebrochen: {abgebrochen}</span>}
          </div>
          {status?.fertig && (
            <p className="mt-2 text-sm font-medium text-green-700">Fertig.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ScopedMonthEmployeeSyncPanel;
